import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography, Table, Button, Input, Switch, InputNumber,
    Space, Card, Tag, Popconfirm, message, Select, Spin,
} from 'antd';
import { PlusOutlined, SaveOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const AI_API_BASE = `${process.env.REACT_APP_API_BASE}/api/ai`;
const SERVICES = ['openai', 'openrouter', 'sarvam', 'assemblyai'];

const getToken = () => {
    const value = `; ${document.cookie}`;
    const parts = value.split('; token=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

const aiApi = axios.create({ baseURL: AI_API_BASE, headers: { 'Content-Type': 'application/json' } });

const AIKeys = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    // keysMap: { [service]: [{ _id, label, apiKey, isActive, priority, _isNew, _rawKey }] }
    const [keysMap, setKeysMap] = useState({});
    const [saving, setSaving] = useState({});

    // ── Fetch ────────────────────────────────────────────────────────────────
    const fetchKeys = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await aiApi.get('/keys', {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const map = {};
            data.forEach(({ service, keys }) => {
                map[service] = keys.map((k) => ({ ...k, _rawKey: '' }));
            });
            setKeysMap(map);
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to load API keys');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchKeys(); }, [fetchKeys]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const updateKey = (service, idx, field, value) => {
        setKeysMap((prev) => {
            const updated = [...prev[service]];
            updated[idx] = { ...updated[idx], [field]: value };
            return { ...prev, [service]: updated };
        });
    };

    const addKey = (service) => {
        setKeysMap((prev) => ({
            ...prev,
            [service]: [
                ...(prev[service] || []),
                { label: '', apiKey: '', isActive: true, priority: (prev[service]?.length || 0), _isNew: true, _rawKey: '' },
            ],
        }));
    };

    const removeKey = (service, idx) => {
        setKeysMap((prev) => {
            const updated = prev[service].filter((_, i) => i !== idx);
            return { ...prev, [service]: updated };
        });
    };

    // ── Save ─────────────────────────────────────────────────────────────────
    const saveService = async (service) => {
        const entries = keysMap[service] || [];

        // Validate
        for (let i = 0; i < entries.length; i++) {
            if (!entries[i].label.trim()) {
                return message.error(`Key #${i + 1} for ${service} is missing a label`);
            }
            if (entries[i]._isNew && !entries[i]._rawKey.trim()) {
                return message.error(`Key #${i + 1} (new) for ${service} requires a key value`);
            }
        }

        setSaving((s) => ({ ...s, [service]: true }));
        try {
            const payload = entries.map((k) => ({
                ...(k._id ? { _id: k._id } : {}),
                label:    k.label,
                // Send the new raw value if edited, else send back the masked placeholder
                // (server will preserve stored value when it detects "...")
                apiKey:   k._rawKey.trim() || k.apiKey,
                isActive: k.isActive,
                priority: k.priority,
            }));

            await aiApi.put('/keys', { service, keys: payload }, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });

            message.success(`Keys for "${service}" saved successfully`);
            await fetchKeys(); // refresh with fresh masked values
        } catch (err) {
            message.error(err.response?.data?.message || `Failed to save keys for ${service}`);
        } finally {
            setSaving((s) => ({ ...s, [service]: false }));
        }
    };

    // ── Columns per-service table ─────────────────────────────────────────────
    const buildColumns = (service) => [
        {
            title: 'Priority',
            dataIndex: 'priority',
            width: 90,
            render: (val, _, idx) => (
                <InputNumber
                    min={0}
                    value={val}
                    onChange={(v) => updateKey(service, idx, 'priority', v ?? 0)}
                    style={{ width: 70 }}
                />
            ),
        },
        {
            title: 'Label',
            dataIndex: 'label',
            render: (val, _, idx) => (
                <Input
                    value={val}
                    placeholder="e.g. primary, backup-1"
                    onChange={(e) => updateKey(service, idx, 'label', e.target.value)}
                    style={{ width: 160 }}
                />
            ),
        },
        {
            title: 'API Key',
            dataIndex: 'apiKey',
            render: (masked, record, idx) => (
                <Input.Password
                    value={record._rawKey || ''}
                    placeholder={record._isNew ? 'Paste new key' : masked}
                    onChange={(e) => updateKey(service, idx, '_rawKey', e.target.value)}
                    style={{ width: 340 }}
                />
            ),
        },
        {
            title: 'Active',
            dataIndex: 'isActive',
            width: 80,
            render: (val, _, idx) => (
                <Switch
                    checked={val}
                    onChange={(v) => updateKey(service, idx, 'isActive', v)}
                />
            ),
        },
        {
            title: '',
            width: 60,
            render: (_, __, idx) => (
                <Popconfirm
                    title="Remove this key?"
                    onConfirm={() => removeKey(service, idx)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
            ),
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
            <Space style={{ marginBottom: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>
                    Dashboard
                </Button>
                <Title level={2} style={{ margin: 0 }}>AI Service API Keys</Title>
            </Space>

            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                Keys are tried in priority order (0 = first). If a key is rejected by the upstream
                service, the next active key is automatically used.
            </Text>

            {SERVICES.map((service) => (
                <Card
                    key={service}
                    title={
                        <Space>
                            <Tag color="blue" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                                {service}
                            </Tag>
                            <Text type="secondary">{(keysMap[service] || []).length} key(s)</Text>
                        </Space>
                    }
                    extra={
                        <Space>
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => addKey(service)}
                            >
                                Add Key
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={!!saving[service]}
                                onClick={() => saveService(service)}
                            >
                                Save
                            </Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Table
                        dataSource={keysMap[service] || []}
                        columns={buildColumns(service)}
                        rowKey={(_, idx) => idx}
                        pagination={false}
                        size="small"
                        locale={{ emptyText: 'No keys configured. Click "Add Key" to add one.' }}
                    />
                </Card>
            ))}
        </div>
    );
};

export default AIKeys;
